/**
 * PRD.305 — the Laboratory Requests group inside the Consultation Prescription
 * canvas.
 *
 * The Prescription canvas represents everything the clinician ordered in this
 * consultation, so laboratory requests sit in it as a GROUP beside Medications
 * — not a tab, not a second card. The group renders only when it has items;
 * the ordering drawers mount unconditionally so the first test can always be
 * added from the canvas's Add Item menu.
 *
 * What it writes is not a prescription. Tests are created as canonical
 * `lab_orders` / `lab_order_tests` through the shared ordering drawer;
 * `patient_prescriptions` is never touched. The models stay separate because a
 * lab test has no dose, frequency, duration or intake timing, and a medicine
 * has no specimen or result to publish.
 *
 * Orders are supplied by the canvas (already visibility-filtered via
 * `selectVisibleLabOrders`) so the canvas's empty state and this group can
 * never disagree about whether anything exists.
 */
import { useMemo, useState } from 'react';
import { usePermissions } from '@helios/auth-firebase-web';
import {
  qPatientById,
  aggregateLabOrderStatus,
  parseTestStatusList,
  type LabOrderRow,
  type LaboratoryCapabilities,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { useInvoiceModuleEnabled } from '../../../hooks/useClinicMainModules';
import { PERM } from '../../../auth/permissionMap';
import { LabStatusBadge, formatLabDate } from '../../laboratory/lab-shared';
import { LabOrderDrawer } from '../../laboratory/LabOrderDrawer';
import { CreateLabOrderDrawer } from '../../laboratory/CreateLabOrderDrawer';

interface PatientRow {
  id: string;
  full_name: string | null;
  clinic_id?: string | null;
}

interface Props {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
  providerId?: string | null;
  /** Already visibility-filtered by the canvas. */
  orders: LabOrderRow[];
  capabilities: LaboratoryCapabilities;
  /** Whether to label the group — the canvas suppresses labels when it holds
   *  only medications, so a lab-free clinic sees the pre-PRD.305 layout. */
  showHeading: boolean;
  /** The canvas's "Add Item → Laboratory Test" action. */
  addOpen: boolean;
  onAddOpenChange: (open: boolean) => void;
}

export function EncounterLabOrdersGroup({
  patientId,
  encounterId,
  visitId = null,
  providerId = null,
  orders,
  capabilities,
  showHeading,
  addOpen,
  onAddOpenChange,
}: Props) {
  const { canAccess } = usePermissions();
  const invoiceEnabled = useInvoiceModuleEnabled();
  const [selected, setSelected] = useState<LabOrderRow | null>(null);

  const { data: patients = [] } = usePowerSyncQuery<PatientRow>(qPatientById(patientId));
  const patient = patients[0] ?? null;

  const rows = useMemo(
    () =>
      orders.map((o) => ({
        order: o,
        aggregate: aggregateLabOrderStatus(parseTestStatusList(o.test_statuses)),
      })),
    [orders],
  );

  const orderContext = useMemo(
    () => ({
      patientId,
      patientName: patient?.full_name ?? 'Patient',
      clinicId: patient?.clinic_id ?? null,
      encounterId,
      visitId,
      orderingProviderId: providerId,
    }),
    [patientId, patient, encounterId, visitId, providerId],
  );

  const selectedLive = useMemo(
    () => (selected ? (orders.find((o) => o.id === selected.id) ?? selected) : null),
    [orders, selected],
  );

  const canBill = invoiceEnabled && canAccess(PERM.billing);

  return (
    <>
      {rows.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {showHeading && (
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                color: 'var(--text-muted)',
              }}
            >
              Laboratory Requests
            </h3>
          )}

          {rows.map(({ order, aggregate }) => (
            <button
              key={order.id}
              type="button"
              onClick={() => setSelected(order)}
              title="Open this laboratory request"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {order.order_number ?? 'Lab request'} · {Number(order.test_count ?? 0)} test
                  {Number(order.test_count ?? 0) === 1 ? '' : 's'}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {formatLabDate(order.ordered_at)}
                  {order.provider_name ? ` · ${order.provider_name}` : ''}
                </span>
              </span>
              <LabStatusBadge status={aggregate} />
            </button>
          ))}
        </section>
      )}

      <LabOrderDrawer
        open={selectedLive != null}
        onOpenChange={(open) => !open && setSelected(null)}
        order={selectedLive}
        capabilities={capabilities}
      />

      {/* Mounted unconditionally (and portalled), so the first test can be added
          from the canvas even while this group is empty. Anchored to the
          consultation, so the request reaches Laboratory Flow immediately. */}
      <CreateLabOrderDrawer
        open={addOpen}
        onOpenChange={onAddOpenChange}
        context={orderContext}
        source="consultation"
        canBill={canBill}
      />
    </>
  );
}

export default EncounterLabOrdersGroup;
